import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select", "panel", "itemList", "item", "count", "focusMode"]
  static values = {
    defaultFocusMode: String,
    countOne: String,
    countOther: String,
    optionsUrl: String,
    previewsUrl: String
  }

  connect() {
    this.sync()
  }

  // Targets can arrive after connect (a morph, or rows appended by
  // insertFullOptions). Each re-syncs on its own arrival; sync() is
  // idempotent, so the repeats are free.
  selectTargetConnected() {
    this.sync()
  }

  panelTargetConnected() {
    this.sync()
  }

  itemTargetConnected() {
    this.sync()
  }

  // The full option list is not in the modal at all: building it walks every
  // candidate's planning states and cross-plan assignments, so the modal
  // renders only the current assignments and this fetches the rest the first
  // time the picker opens ($select2:opening). The payload is APPENDED around
  // the live select's existing options -- never swapped -- so anything
  // selected or typed while the fetch was in flight is untouched.
  loadFullOptions() {
    if (!this.optionsUrlValue || this.optionsRequested) return

    this.optionsRequested = true
    fetch(this.optionsUrlValue, {headers: {Accept: "text/html"}})
      .then((response) => {
        if (!response.ok) throw new Error(`people options failed: ${response.status}`)
        return response.text()
      })
      .then((html) => this.insertFullOptions(html))
      .catch(() => {
        this.optionsRequested = false
      })
  }

  insertFullOptions(html) {
    if (!this.hasSelectTarget) return

    const payload = new DOMParser().parseFromString(html, "text/html")
    const select = this.selectTarget
    const selectedValues = new Set(Array.from(select.selectedOptions).map((option) => option.value))
    select.querySelector("option[data-people-options-loading]")?.remove()

    // The server already excludes current assignments, but the selection can
    // change between render and fetch -- skip any value the select knows.
    const knownValues = new Set(Array.from(select.options).map((option) => option.value))
    for (const node of Array.from(payload.querySelectorAll("[data-people-options-source] > *"))) {
      if (node.matches("option")) {
        if (!knownValues.has(node.value)) select.appendChild(node)
        continue
      }
      for (const option of Array.from(node.querySelectorAll("option"))) {
        if (knownValues.has(option.value)) option.remove()
      }
      if (node.querySelector("option")) select.appendChild(node)
    }

    // The detached payload is a single select, so the browser auto-selects the
    // first option in each optgroup as preceding options move out of it. Reset
    // the completed live multiple select to exactly its pre-append selection.
    for (const option of Array.from(select.options)) {
      option.selected = selectedValues.has(option.value)
    }

    this.sync()
    this.refreshOpenResults()
  }

  // select2 re-reads the native select's options on every query, so appended
  // options show up on the next keystroke -- but a dropdown that is already
  // open is showing stale results until then. Poking its search field
  // re-queries (select2 binds "keyup.search input.search" on the selection).
  // Only while open: the same poke on a CLOSED picker makes select2 reopen
  // it, popping up a dropdown the user already dismissed.
  refreshOpenResults() {
    if (!this.element.querySelector(".select2-container--open")) return

    const search = this.element.querySelector(".select2-search__field")
    if (!search) return

    search.dispatchEvent(new Event("input", {bubbles: true}))
  }

  sync() {
    if (!this.hasPanelTarget || !this.hasSelectTarget) return

    const selectedValues = this.selectedValues()
    this.loadMissingItems(selectedValues)
    let visibleCount = 0

    this.itemTargets.forEach((item) => {
      const selected = selectedValues.has(item.dataset.selectionValue)
      item.classList.toggle("hidden", !selected)

      if (selected) {
        visibleCount += 1
        this.applyStatus(item)
      }
    })

    this.panelTarget.classList.toggle("hidden", visibleCount === 0)
    if (this.hasCountTarget) {
      this.countTarget.textContent = this.countText(visibleCount)
    }
  }

  // The panel's rows are not shipped for every candidate any more -- there were
  // 299 of them on a wedding-sized plan, three quarters of the options payload,
  // for rows that are only ever shown one or two at a time. The modal still
  // renders the rows for the current assignments, so opening it warns about
  // what is already there with no request at all; a row for someone newly
  // SELECTED is fetched here.
  //
  // requestedItems is what keeps this out of a loop. Appending a row fires
  // itemTargetConnected -> sync(), and most selections legitimately answer with
  // no row at all -- somebody with no conflict and no planning warning -- which
  // would otherwise be asked for again on every change event for the life of
  // the modal.
  loadMissingItems(selectedValues) {
    if (!this.hasPreviewsUrlValue || !this.previewsUrlValue || !this.hasItemListTarget) return

    this.requestedItems ||= new Set()
    const known = new Set(this.itemTargets.map((item) => item.dataset.selectionValue))
    const missing = Array.from(selectedValues).filter(
      (value) => !known.has(value) && !this.requestedItems.has(value)
    )
    if (missing.length === 0) return

    missing.forEach((value) => this.requestedItems.add(value))

    const url = new URL(this.previewsUrlValue, window.location.origin)
    missing.forEach((value) => url.searchParams.append("selections[]", value))

    fetch(url, {headers: {Accept: "text/html"}})
      .then((response) => {
        if (!response.ok) throw new Error(`people conflict previews failed: ${response.status}`)
        return response.text()
      })
      .then((html) => {
        this.appendItems(new DOMParser().parseFromString(html, "text/html"))
        this.sync()
      })
      .catch(() => {
        // Forget the failed ones rather than never warning about those people
        // again: the next change event retries.
        missing.forEach((value) => this.requestedItems.delete(value))
      })
  }

  appendItems(payload) {
    if (!this.hasItemListTarget) return

    const known = new Set(this.itemTargets.map((item) => item.dataset.selectionValue))
    for (const item of Array.from(payload.querySelectorAll("[data-people-conflict-items-source] > *"))) {
      if (known.has(item.dataset.selectionValue)) continue

      this.itemListTarget.appendChild(item)
    }
  }

  selectedValues() {
    return new Set(
      Array.from(this.selectTarget.selectedOptions)
        .map((option) => option.value)
        .filter((value) => value !== "")
    )
  }

  applyStatus(item) {
    const status = this.statusFor(item)
    item.dataset.previewStatus = status

    const title = item.querySelector("[data-conflict-preview-title]")
    if (title) {
      title.textContent = item.dataset[`${status}Title`] || ""
    }
  }

  removeItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const item = event.currentTarget.closest("[data-selection-value]")
    if (!item) return

    this.deselectValue(item.dataset.selectionValue)
    this.sync()
  }

  deselectValue(value) {
    const option = Array.from(this.selectTarget.options).find((candidate) => candidate.value === value)
    if (!option) return

    option.selected = false
    this.dispatchSelectChange()
  }

  dispatchSelectChange() {
    if (window.jQuery) {
      window.jQuery(this.selectTarget).trigger("change.select2").trigger("change")
    } else {
      this.selectTarget.dispatchEvent(new Event("change", {bubbles: true}))
    }
  }

  statusFor(item) {
    if (item.dataset.planningWarning === "true") return "planning"
    if (this.currentFocusMode() === "full_focus") return "problem"
    if (Number(item.dataset.relatedFocusCount || 0) > 0) return "hint"

    return "overlap"
  }

  currentFocusMode() {
    const selectedFocusMode = this.focusModeTargets.find((target) => {
      if (target.type === "radio") return target.checked

      return true
    })

    if (selectedFocusMode?.value) {
      return selectedFocusMode.value
    }

    return this.defaultFocusModeValue
  }

  countText(count) {
    if (count === 1) return this.countOneValue

    return this.countOtherValue.replace("%{count}", count)
  }
}
